import React, { useState, useEffect, useRef } from "react";
import { browserHistory } from "react-router";
import { Link } from "react-router";
import PropTypes from "prop-types";
import ConflictFormRule from "./conflict-form-rule";
import ConflictFormSchedule from "./conflict-form-schedule";
import LoadingIndicatorHeader from "../loading-indicator/loading-indicator-header";
import moment from "moment";
import util from "../util/util";

const jQuery = window.jQuery;

export default function ConflictForm(props) {
  const [formData, setFormData] = useState({ loaded: false });
  const [selectedRuleIndex, setSelectedRuleIndex] = useState(0);
  const checkboxRef = useRef(null);

  useEffect(() => {
    getConflicts();
  }, [props.location]);

  // get the conflict to resolve (one at a time);
  const getConflicts = () => {
    util.getConflicts().then(data => {
      const result = data.results[0];
      const conflicts = result ? result.conflicting_rules : []; // default empty array so we can display `no results`;

      // create an array of all rules in the conflict;
      const rules = [];

      // why convert this data?
      // to convert the fields into Numbers where needed;
      // to convert param names with underscores to camelCase for consistency;
      conflicts.forEach(conflictingRule => {
        rules.push({
          id: Number(conflictingRule.id),
          name: conflictingRule.name,
          pod: conflictingRule.pod,
          campaign: conflictingRule.campaign,
          dateEnd: conflictingRule.end_date,
          dateStart: conflictingRule.start_date,
          timeslots: conflictingRule.timeslots,
          shows: conflictingRule.shows,
          repeats: Number(conflictingRule.frequency),
          conflictOccurrences: conflictingRule.conflicting_rules_occurrences
        });
      });

      // wait a little bit (so the user can track what’s going on);
      setTimeout(() => {
        // put the button back into the ready state;
        // it may be in the loading state if we’re dealing with lots of conflicts;
        jQuery(`.btn-primary`).button(`reset`);

        const conflict = result ? result.conflict : null;
        const date = moment.utc(conflict);

        // set state data so the user can interact with the form;
        setFormData({
          count: data.count,
          id: conflict,
          dateMoment: date,
          timeslot: date.format(`HH:mm`),
          date: date.format(`ddd MM/DD/YYYY`),
          rules: rules,
          loaded: true,
          selectedRuleIndex: 0
        });

        // update page-header conflict count;
        window.updateConflictCount();
      }, util.delayDetail);
    });
  };

  // resolve the conflict by creating an exception;
  const onSubmit = () => {
    const id = formData.id;
    const rule = formData.rules[selectedRuleIndex].id;
    const report = {
      selected_rule: rule,
      apply_all: checkboxRef.current.checked
    };

    // put the button into a processing state;
    const $button = jQuery(`.btn-primary`).button(`loading`);

    // create the exception;
    util
      .saveException(id, report)
      .fail(() => {
        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);
      })
      .then(() => {
        // now get the new conflict (if any);
        util.getConflicts().then(data => {
          const newURL =
            data && data.count
              ? util.returnConflictFormUrl(data.count)
              : util.returnRuleListUrl();

          // wait a little bit (so the user can track what’s going on);
          setTimeout(() => {
            // load the next page (rule list or conflict form);
            browserHistory.push(newURL);

            // tell the header to update the conflict count;
            window.updateConflictCount();
          }, util.delayForm);
        });
      });
  };

  if (formData.loaded === false) {
    return <LoadingIndicatorHeader />;
  } else if (formData.rules.length === 0) {
    return <h4 className="page__title">No Conflicts to Resolve</h4>;
  } else {
    const conflictOccurrences =
      formData.rules[selectedRuleIndex].conflictOccurrences;

    return (
      <form action="#">
        <div className="row">
          <div className="col-sm-6 col-sm-push-6">
            <p className="page__actions">
              <Link
                to={util.returnRuleFormUrl(
                  formData.rules[selectedRuleIndex].id
                )}
                className="btn btn-default"
              >
                Cancel and Edit Rule
              </Link>
            </p>
          </div>
          <div className="col-sm-6 col-sm-pull-6">
            <h4 className="page__title">Resolve Conflicts</h4>
          </div>
        </div>

        <hr />

        <p>
          Choose which rule should take precendence for the{" "}
          <strong>{formData.timeslot}</strong> time slot on{" "}
          <strong>{formData.date}</strong>:
        </p>

        {formData.rules.map((rule, index) => {
          return (
            <div className="radio" key={index}>
              <label>
                <input
                  type="radio"
                  name="rule"
                  value={rule.id}
                  checked={selectedRuleIndex === index}
                  onChange={() => {
                    setSelectedRuleIndex(index);
                    checkboxRef.current.checked = false;
                  }}
                />{" "}
                {rule.name}
              </label>
            </div>
          );
        })}

        <div className={formData.count > 1 ? "checkbox" : "hidden"}>
          <label>
            <input
              type="checkbox"
              ref={checkboxRef}
              disabled={conflictOccurrences === 1}
            />
            Apply this override to all time slots where these rules conflict (
            {conflictOccurrences} occurrence
            {conflictOccurrences > 1 ? "s" : null}
            ).
          </label>
        </div>

        <hr />

        <p className="page__actions">
          <button
            type="button"
            className="btn btn-primary"
            data-loading-text="<i class='fa fa-spinner fa-spin'></i> <i class='fa fa-floppy-o'></i> Resolving Conflict"
            onClick={() => onSubmit()}
          >
            <i className="fa fa-floppy-o" aria-hidden="true"></i>
            Resolve Conflict
          </button>
        </p>

        <hr />

        <ConflictFormRule rule={formData.rules[selectedRuleIndex]} />

        <hr className="spacer" />

        <ConflictFormSchedule
          day={formData.dateMoment}
          timeslot={formData.timeslot}
        />
      </form>
    );
  }
}

ConflictForm.propTypes = {
  params: PropTypes.object
};
